import { useEffect, useMemo, useRef } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import { useTenant } from '@/providers/TenantProvider'
import { useAuth } from '@/providers/AuthProvider'
import { usePatient } from '@/hooks/usePatients'
import { usePatientRecord } from '@/hooks/usePatientRecord'
import { usePatientProducts } from '@/hooks/useProducts'
import { usePatientLisResults } from '@/hooks/useLisOrders'
import { formatSex, isPatientNotFound, patientInitials, patientName } from '@/lib/patient'
import { procedureStatusMeta } from '@/lib/procedureStatus'
import { cn } from '@/lib/utils'
import {
  AtAGlance,
  ClinicalCard,
  dayZeroText,
  DonorsSection,
  HistoryTimeline,
  IdentityCard,
  LisSection,
  productsByRun,
  ProductsSection,
  RunProducts,
  runDonor,
  StorageSection,
} from '@/components/patient/record/PatientRecordSections'
import {
  formatDateTime,
  formatLongDate,
  formatScheduled,
  HeaderFact,
  INK,
  MUTED,
  PrintModeContext,
  RecordButton,
  RecordTable,
  SectionCard,
  Td,
  TonePill,
} from '@/components/record/RecordParts'

/**
 * `/patients/:mrn/print` — the patient's full record as a printable summary
 * (z8nrz7g57g).
 *
 * Inside the authenticated routes but outside Layout, like the purchase-order
 * and storage-transfer print views: the app shell holds the page in a
 * fixed-height scrolling pane, which a printer cuts at the first screen, and
 * the sticky section bar and sideways-scrolling tables overlap the paper. Here
 * the record is the whole document: every history event and laboratory panel,
 * tables that wrap to the page, no controls, and page breaks that do not split
 * a row or a small card. `?auto=1` (how the patient page's Print summary
 * opens it) prints as soon as everything has loaded.
 */
export function PatientSummaryPrintPage() {
  const { isTenantResolved } = useTenant()
  if (!isTenantResolved) return <Centered />
  return <PrintView />
}

const PRINT_CSS = `
@page { size: A4 landscape; margin: 10mm; }
@media print {
  html, body { background: #fff !important; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .record-no-print, .tsqd-parent-container, .tsqd-open-btn-container { display: none !important; }
  .record-print-doc { padding: 0 !important; max-width: none !important; }
  /* A long section may run over pages, so it prints without its frame: a frame
     cut by a page break leaves an empty box behind. Its heading stays with
     what follows it. */
  .record-card { border-color: transparent !important; box-shadow: none !important; }
  .record-card > div:first-child, h2 { break-after: avoid; }
  /* Small cards keep their frame and never split. */
  .record-print-grid .record-card, #donors .record-card, .record-keep {
    border-color: #E4E7EC !important; break-inside: avoid;
  }
  .record-panel { break-inside: avoid; }
  tr, li { break-inside: avoid; }
  a { color: #175CD3 !important; }
}
`

function PrintView() {
  const { mrn = '' } = useParams<{ mrn: string }>()
  const [params] = useSearchParams()
  const { user } = useAuth()
  const patient = usePatient(mrn)
  const record = usePatientRecord(mrn)
  const productsQuery = usePatientProducts(mrn)
  const lis = usePatientLisResults(mrn)
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data])
  const runProducts = useMemo(() => productsByRun(products), [products])

  // Everything the document shows has answered (an error counts: the section says so).
  const settled = !patient.isPending && !record.isPending && !productsQuery.isPending && !lis.isPending
  const printed = useRef(false)
  useEffect(() => {
    if (!settled || printed.current || params.get('auto') !== '1') return
    printed.current = true
    // A beat for the last layout and the chart to paint before the dialog opens.
    const t = window.setTimeout(() => window.print(), 600)
    return () => window.clearTimeout(t)
  }, [settled, params])

  const p = patient.data
  const clinical = record.data?.clinical
  const weight = clinical?.recipient_weight_kg ?? clinical?.last_weight_kg
  const fullName = (p ? patientName(p) : null) ?? mrn
  const recipientKind = [clinical?.transplant_type?.value, clinical?.planned_donor_type?.value].join(' ')
  const procedures = p?.procedures ?? []
  const procedureName = (id: string) => procedures.find((x) => x.id === id)?.procedureTemplate.name ?? null
  const facts = [
    { label: 'DOB', value: formatLongDate(p?.birthDate) },
    { label: 'Age', value: p?.age != null ? `${p.age} yrs` : null },
    { label: 'Sex', value: formatSex(p?.sex) },
    { label: 'ABO / Rh', value: clinical?.recipient_abo_rh?.value },
    { label: 'Weight', value: weight ? `${weight.value} kg` : null },
    { label: 'Diagnosis', value: clinical?.diagnosis?.value },
    { label: 'Day 0', value: dayZeroText(clinical?.planned_infusion_date) },
  ].filter((f) => f.value)

  return (
    <PrintModeContext.Provider value>
      <style>{PRINT_CSS}</style>
      <div className={cn('min-h-screen bg-[#F4F6F9] print:bg-white', INK)}>
        <div className='record-no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[#E4E7EC] bg-white px-6 py-3'>
          <Link to={`/patients/${encodeURIComponent(mrn)}`} className={cn('inline-flex items-center gap-1.5 text-sm hover:text-[#1D2939]', MUTED)}>
            <ArrowLeft className='h-4 w-4' />
            Back to the patient
          </Link>
          <RecordButton primary onClick={() => window.print()} disabled={!settled}>
            {settled ? <Printer className='h-4 w-4' /> : <Loader2 className='h-4 w-4 animate-spin' />}
            {settled ? 'Print' : 'Preparing…'}
          </RecordButton>
        </div>

        <main className='record-print-doc mx-auto flex max-w-[1392px] flex-col gap-5 p-6'>
          {!settled ? (
            <Centered />
          ) : (
            <>
              <header className='record-keep flex flex-wrap items-center justify-between gap-6 rounded-[10px] border border-[#E4E7EC] bg-white px-6 py-5'>
                <div className='flex items-center gap-4'>
                  <div className='flex h-14 w-14 flex-none items-center justify-center rounded-full bg-[#1F7BC6] text-xl font-bold text-white'>
                    {patientInitials(p ? patientName(p) : null, mrn)}
                  </div>
                  <div className='flex flex-col gap-1'>
                    <div className={cn('text-xs font-bold uppercase tracking-[0.1em]', MUTED)}>Patient summary</div>
                    <h1 className='m-0 text-[26px] font-bold leading-tight'>{fullName}</h1>
                    <div className={cn('font-mono text-[12.5px]', MUTED)}>MRN {mrn}</div>
                  </div>
                </div>
                <div className='flex flex-wrap gap-7'>
                  {facts.map((f) => (
                    <HeaderFact key={f.label} label={f.label} value={f.value} />
                  ))}
                </div>
                <div className='flex flex-col items-end gap-2'>
                  <div className='flex gap-2'>
                    <TonePill tone='green'>Active patient</TonePill>
                    {/autolog/i.test(recipientKind) && <TonePill tone='blue'>Autologous recipient</TonePill>}
                    {/allogen/i.test(recipientKind) && <TonePill tone='blue'>Allogeneic recipient</TonePill>}
                  </div>
                  <div className={cn('text-xs', MUTED)}>
                    Printed {formatDateTime(new Date().toISOString())}
                    {user?.fullName || user?.email ? ` by ${user.fullName || user.email}` : ''}
                  </div>
                </div>
              </header>

              <section className='record-print-grid grid grid-cols-3 gap-4'>
                <IdentityCard patient={p} mrn={mrn} isError={patient.isError && !isPatientNotFound(patient.error)} />
                <ClinicalCard record={record.data} isError={record.isError} />
                <AtAGlance products={productsQuery.isError ? undefined : products} lis={lis.isError ? undefined : lis.data} record={record.data} />
              </section>

              <HistoryTimeline
                record={record.data}
                lis={lis.isError ? undefined : lis.data}
                lisError={lis.isError}
                isLoading={false}
                isError={record.isError}
              />

              <SectionCard title='Transplants & procedures' subtitle='Date/time, donor and the products (DIN) each one used' flush>
                {procedures.length === 0 ? (
                  <p className={cn('m-0 px-6 pb-5 text-sm', MUTED)}>No procedures opened for this patient yet.</p>
                ) : (
                  <RecordTable head={['Procedure', 'Type', 'Date / time', 'Donor', 'Products (DIN)', 'Progress', 'Status']}>
                    {procedures.map((r) => {
                      const meta = procedureStatusMeta(r)
                      return (
                        <tr key={r.id}>
                          <Td className='font-semibold'>{r.procedureTemplate.name}</Td>
                          <Td>{r.procedureTemplate.type ?? '—'}</Td>
                          <Td>
                            {formatDateTime(r.startedAt)}
                            {r.scheduledDate && <span className={cn('block', MUTED)}>Scheduled {formatScheduled(r.scheduledDate)}</span>}
                          </Td>
                          <Td>{runDonor(record.data?.donors ?? [], r.id) ?? '—'}</Td>
                          <Td>
                            <RunProducts products={runProducts.get(r.id)} />
                          </Td>
                          <Td>{r.progress && r.progress.total > 0 ? `${r.progress.completed} / ${r.progress.total} steps` : 'No steps'}</Td>
                          <Td>
                            <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold', meta.badge)}>
                              <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
                              {meta.label}
                            </span>
                          </Td>
                        </tr>
                      )
                    })}
                  </RecordTable>
                )}
              </SectionCard>

              <ProductsSection products={products} isLoading={false} isError={productsQuery.isError} donors={record.data?.donors ?? []} />
              <LisSection results={lis.data} isLoading={false} isError={lis.isError} />
              <DonorsSection
                record={record.data}
                isLoading={false}
                isError={record.isError}
                procedureName={procedureName}
                products={products}
                patient={{
                  name: p ? patientName(p) : null,
                  mrn,
                  age: p?.age,
                  sex: formatSex(p?.sex),
                  abo: clinical?.recipient_abo_rh?.value ?? null,
                  weight: weight ? `${weight.value} kg` : null,
                }}
              />
              <StorageSection products={products} record={record.data} isError={productsQuery.isError} />
            </>
          )}
        </main>
      </div>
    </PrintModeContext.Provider>
  )
}

function Centered() {
  return (
    <div className='flex min-h-[50vh] items-center justify-center'>
      <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
    </div>
  )
}
